/**
 * Whether this browser can render the 3D globe at all.
 *
 * Checked before the 3D control is offered, so a machine without WebGL gets a
 * disabled button with an explanation rather than a ~6 MB download followed by
 * a blank canvas. The canvas factory is injectable purely so this is testable
 * without a DOM.
 */
export const supportsWebGL = (
  createCanvas: () => HTMLCanvasElement = () =>
    document.createElement('canvas'),
): boolean => {
  try {
    const canvas = createCanvas()
    // Exactly the two names Cesium itself asks for: it requests webgl2 and
    // falls back to webgl when WebGL2RenderingContext is missing, and never
    // tries `experimental-webgl`. Accepting that here would pass a browser
    // Cesium then refuses.
    const contextAttributes = { failIfMajorPerformanceCaveat: true }
    const context =
      canvas.getContext('webgl2', contextAttributes) ??
      canvas.getContext('webgl', contextAttributes)
    if (!context) return false

    // Browsers cap how many live WebGL contexts a page may hold, and this
    // probe's context is never drawn to, so hand it straight back.
    const loseContext = (context as WebGLRenderingContext).getExtension?.(
      'WEBGL_lose_context',
    )
    loseContext?.loseContext()

    return true
  } catch {
    // Browsers with WebGL disabled by policy throw here rather than returning
    // null, so a failure to ask is itself the answer.
    return false
  }
}
